import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, Edit2, Trash2, Search, RefreshCw, Users, Building2, Award, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grade colors
const GRADE_COLORS = {
  'L1': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-l-emerald-500', badge: 'bg-emerald-500' },
  'L2': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-l-blue-500', badge: 'bg-blue-500' },
  'L3': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-l-purple-500', badge: 'bg-purple-500' },
  'L4': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-l-orange-500', badge: 'bg-orange-500' },
  'L5': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-l-red-500', badge: 'bg-red-500' },
  'L6': { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-l-pink-500', badge: 'bg-pink-500' },
};

const getGradeStyle = (grade) => GRADE_COLORS[grade] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-l-gray-400', badge: 'bg-gray-500' };

function ProfileManagement({ user, onLogout }) {
  const [profiles, setProfiles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  
  const [formData, setFormData] = useState({
    profile_name: '',
    department: '',
    allowed_grades: [],
    description: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [deptRes, gradeRes, profileRes] = await Promise.allSettled([
        axios.get(`${API}/org-structure/departments`, { headers }),
        axios.get(`${API}/org-structure/grades`, { headers }),
        axios.get(`${API}/org-structure/profiles`, { headers })
      ]);

      setDepartments(deptRes.status === 'fulfilled' ? (deptRes.value.data.departments || []) : []);
      setGrades(gradeRes.status === 'fulfilled' ? (gradeRes.value.data.grades || []) : []);
      setProfiles(profileRes.status === 'fulfilled' ? (profileRes.value.data.profiles || []) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter profiles
  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = !searchTerm || 
      (profile.profile_name || profile.name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDept === 'all' || profile.department === filterDept;
    return matchesSearch && matchesDept;
  });

  // Statistics
  const stats = {
    total: profiles.length,
    departments: [...new Set(profiles.map(p => p.department))].length,
    grades: grades.length,
    filtered: filteredProfiles.length
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingProfile) {
        await axios.put(`${API}/org-structure/profiles/${editingProfile.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profile updated successfully!');
      } else {
        await axios.post(`${API}/org-structure/profiles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profile created successfully!');
      }
      
      setShowForm(false);
      setEditingProfile(null);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      profile_name: profile.profile_name || profile.name || '',
      department: profile.department || '',
      allowed_grades: profile.allowed_grades || [],
      description: profile.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete profile "${profile.profile_name || profile.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/org-structure/profiles/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile deleted successfully!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({ profile_name: '', department: '', allowed_grades: [], description: '' });
    setEditingProfile(null);
  };

  const toggleGrade = (gradeKey) => {
    setFormData(prev => {
      const currentGrades = prev.allowed_grades || [];
      const isSelected = currentGrades.includes(gradeKey);
      return {
        ...prev,
        allowed_grades: isSelected 
          ? currentGrades.filter(g => g !== gradeKey)
          : [...currentGrades, gradeKey]
      };
    });
  };

  const getGradeKey = (grade) => grade.grade || grade.grade_code || grade.id;
  const getDeptName = (code) => departments.find(d => d.code === code)?.name || code;

  const canEdit = user?.role?.toLowerCase() === 'admin' || 
                  user?.role?.toLowerCase() === 'hr' || 
                  user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>👤 Profile Management</h1>
            <p className="text-gray-600 mt-2">Manage job profiles with department and grade mappings</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canEdit && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: '#2b3380'}}
              >
                <Plus className="w-5 h-5" />
                Add Profile
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Users className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Profiles</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.departments}</p>
              <p className="text-sm text-gray-500">Departments</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{stats.grades}</p>
              <p className="text-sm text-gray-500">Grade Levels</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <Search className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{stats.filtered}</p>
              <p className="text-sm text-gray-500">Filtered Results</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search profiles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept.code || dept.id} value={dept.code}>{dept.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Profiles List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading profiles...</p>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No profiles found</p>
            <p className="text-gray-400">
              {searchTerm || filterDept !== 'all' 
                ? 'Try different search terms or filters' 
                : canEdit ? 'Click "Add Profile" to create one' : 'Check back later'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProfiles.map((profile) => {
              const profileName = profile.profile_name || profile.name || 'Unnamed';
              const firstGrade = profile.allowed_grades?.[0];
              const borderColor = firstGrade ? getGradeStyle(firstGrade).border : 'border-l-gray-400';
              
              return (
                <div 
                  key={profile.id} 
                  className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${borderColor}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{profileName}</h3>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {getDeptName(profile.department)}
                        </span>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(profile)}
                          className="p-2 hover:bg-blue-50 rounded text-blue-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(profile)}
                          className="p-2 hover:bg-red-50 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {profile.description && (
                    <p className="text-gray-700 mb-4 leading-relaxed">{profile.description}</p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                    <span className="text-sm text-gray-500 mr-2">Allowed Grades:</span>
                    {profile.allowed_grades?.map(grade => {
                      const style = getGradeStyle(grade);
                      return (
                        <span key={grade} className={`px-3 py-1 rounded-full text-xs font-medium text-white ${style.badge}`}>
                          {grade}
                        </span>
                      );
                    })}
                    {(!profile.allowed_grades || profile.allowed_grades.length === 0) && (
                      <span className="text-sm text-gray-400">No grades assigned</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {editingProfile ? '✏️ Edit Profile' : '👤 Add New Profile'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Profile Name *</label>
                      <input
                        type="text"
                        value={formData.profile_name}
                        onChange={(e) => setFormData({...formData, profile_name: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Sales Executive"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Department *</label>
                      <select
                        value={formData.department}
                        onChange={(e) => setFormData({...formData, department: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.code || dept.id} value={dept.code}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Allowed Grades *</label>
                      <p className="text-xs text-gray-500 mb-3">Click grades to select/deselect</p>
                      <div className="flex gap-2 flex-wrap">
                        {grades.map(grade => {
                          const gradeKey = getGradeKey(grade);
                          const isSelected = formData.allowed_grades.includes(gradeKey);
                          const style = getGradeStyle(gradeKey);
                          return (
                            <button
                              key={gradeKey}
                              type="button"
                              onClick={() => toggleGrade(gradeKey)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all border-2 flex items-center gap-2 ${
                                isSelected
                                  ? `${style.bg} ${style.text} border-current`
                                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              {isSelected && <Check className="w-4 h-4" />}
                              {gradeKey}
                            </button>
                          );
                        })}
                      </div>
                      {formData.allowed_grades.length > 0 && (
                        <p className="text-sm text-green-600 mt-3">
                          ✓ Selected: {formData.allowed_grades.join(', ')}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Brief description of this profile..."
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Profiles are used to map job designations to specific grades within departments.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <Users className="w-5 h-5" />
                        {submitting ? 'Saving...' : (editingProfile ? 'Update Profile' : 'Create Profile')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ProfileManagement;
